'use client'

import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react'
import { useLayoutEffect, useRef, useState } from 'react'

import { Icon } from '@/components/ui/icon'
import { ORDER_RESTOCKING_FEE_MAX_RATE, ORDER_RESTOCKING_FEE_MIN_RATE } from '@/config/constants'

/**
 * "?" info control for the restocking-fee cancellation policy. Customers pay
 * the FULL total at checkout; the fee only exists as a refund withholding on
 * cancellation (B1 decision, 2026-07-19). Shared by the cart drawer, the /cart
 * page, and checkout Review so the copy and the numbers can never drift.
 *
 * The popover panel renders through react-aria's own portal to
 * document.body, so it does not inherit the trigger's data-theme ancestor
 * via the normal DOM cascade (the same problem the drawer/modal self-scoping
 * pattern solves). Since this component is shared across a v2 scope (cart
 * drawer, /cart page) and a still-v1 one (checkout Review), it can't hardcode
 * a theme; instead it reads its trigger's nearest [data-theme] ancestor at
 * render time and stamps that value on the portaled content, so it always
 * matches whichever surface it was opened from.
 */
export function RestockingFeeInfo() {
  const minPct = Math.round(ORDER_RESTOCKING_FEE_MIN_RATE * 100)
  const maxPct = Math.round(ORDER_RESTOCKING_FEE_MAX_RATE * 100)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const [ancestorTheme, setAncestorTheme] = useState<string | undefined>(undefined)

  useLayoutEffect(() => {
    setAncestorTheme(
      triggerRef.current?.closest('[data-theme]')?.getAttribute('data-theme') ?? undefined
    )
  }, [])

  return (
    <Popover>
      <PopoverTrigger>
        <button
          ref={triggerRef}
          type="button"
          aria-label="About the restocking fee"
          className="inline-flex items-center justify-center text-muted hover:text-foreground transition-colors"
        >
          <Icon icon="question-circle" className="text-base" />
        </button>
      </PopoverTrigger>
      <PopoverContent data-theme={ancestorTheme}>
        <div className="max-w-xs p-3 text-sm space-y-1.5">
          <p className="font-semibold text-foreground">Restocking fee</p>
          <p className="text-muted">
            You pay the full order total at checkout. If you cancel your order after payment,
            a restocking fee of {minPct}-{maxPct}% of the order total is withheld from your refund.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  )
}
